import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import { formatCurrency, sanitizeEmailArray } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { useOrderDocumentList } from "~/hooks/api/v2/orderDocument/useOrderDocumentList";
import { usePaymentList } from "~/hooks/api/v2/payment/usePaymentList";
import { Badge, BadgeGreen, BadgeIndigo } from "../../badge/Badge";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useOrderEventList } from "../../../hooks/api/v2/orderEvent/useOrderEventList";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCreditPaymentStatus } from "../useSellarCreditPaymentStatus";
import { OrderContentConnectionsType } from "../../../pages/order/OrderContent/OrderContent";
import { BuyerOnly } from "../../authFilter/BuyerOnly";
import { SupplierOnly } from "../../authFilter/SupplierOnly";
import { SendPaymentLinkModal } from "../../../pages/order/modals/SendPaymentLinkModal";
import { OrderRequestCardPaymentModal } from "../../../pages/order/modals/OrderRequestCardPaymentModal";
import { PaymentFailedDialog } from "../../../pages/stockList/checkoutV2/lib/PaymentFailedDialog";
import { CardPaymentWithDepositFailedDialog } from "../../../pages/stockList/checkoutV2/lib/CardPaymentWidthDepositFailedDialog";
import { CardChargeRemainingBalancePaymentFailedDialog } from "../../../pages/stockList/checkoutV2/lib/CardChargeRemainingBalancePaymentFailedDialog";
import { PaymentChangeDialog } from "../OrderActionPanel";
import { SellarCreditAddToTabButton } from "../Buttons/SellarCreditAddToTabButton";
import { getOrderStage, OrderStage } from "../helpers";
import { StatusRow } from "./StatusRow";
import { RyftPaymentStatus } from "./RyftPaymentStatus";
import { PAYMENT_LINK_REF } from "../../../constants/routes";

const DATE_FORMAT = "DD/MM/YY HH:mm";

// ─── Sellar Credit badge (reused across several states) ──────────────────────

const SellarCreditBadge = ({ text = "Sellar Credit" }: { text?: string }) => (
  <Badge
    icon="SELLAR_NEW"
    className="shrink-0 bg-linear-to-br from-[#B557F1] to-[#D213A6] text-white"
  >
    {text}
  </Badge>
);

// ─── Sellar Credit payment status ────────────────────────────────────────────

type SellarCreditPaymentStatusProps = {
  order: OrderGetResponseType;
  isBuyer: boolean;
  isSupplier: boolean;
};

/**
 * Handles all Sellar Credit payment states for both buyer and supplier.
 * Returns null when no status should be shown (e.g. pre-confirmed, no tab yet).
 */
const SellarCreditPaymentStatus = ({
  order,
  isBuyer,
  isSupplier,
}: SellarCreditPaymentStatusProps): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const stage = getOrderStage(order);

  const buyerName =
    order.Retailer.Business.name ||
    order.Retailer.Business.registeredCompanyName;

  const {
    isProcessed: orderHasBeenProcessed,
    createdAt: sellarCreditPaymentCreatedAt,
  } = useSellarCreditPaymentStatus({ orderId: order.id });

  const orderDocumentQuery = useOrderDocumentList(
    {
      limit: 50,
      query: [
        isSupplier && {
          type: "SupplierId",
          modifier: "EQUALS",
          value: business?.Supplier?.id,
        },
        isBuyer && {
          type: "RetailerId",
          modifier: "EQUALS",
          value: business?.Retailer?.id,
        },
        { type: "OrderId", modifier: "EQUALS", value: order.id },
      ].filter(Boolean),
    },
    { enabled: true },
  );

  const invoice = orderDocumentQuery.data?.pages[0]?.find(
    (d) => d.type === "invoice",
  );
  const invoicePaidAt = invoice?.paidAt ?? null;

  // Pre-confirmed: prompt buyer to add to tab
  const preConfirmedStages: OrderStage[] = ["Accepted", "Requested"];

  // ── Supplier states ────────────────────────────────────────────────────────

  if (isSupplier) {
    if (invoicePaidAt) {
      return (
        <StatusRow icon="SELLAR_NEW" badge={<SellarCreditBadge />}>
          <span>
            <span className="font-semibold">Paid</span> on{" "}
            {dayjs(invoicePaidAt).format(DATE_FORMAT)}
          </span>
        </StatusRow>
      );
    }
    if (order.dispatchedAt) {
      return (
        <StatusRow icon="SELLAR_NEW" badge={<SellarCreditBadge />}>
          <span className="font-semibold">Funds processing</span>
        </StatusRow>
      );
    }

    if (preConfirmedStages.includes(stage) && !orderHasBeenProcessed) {
      return (
        <StatusRow>
          <div className="flex flex-col gap-2">
            <span className="font-semibold">
              Sellar Credit order can't be finalised yet
            </span>
            <span>
              {buyerName} needs to successfully add this order to their Sellar
              Credit tab before you can process the order.
            </span>
          </div>
        </StatusRow>
      );
    }

    return null;
  }

  // ── Buyer states ───────────────────────────────────────────────────────────

  // Buyer settled with Two
  if (invoicePaidAt) {
    return (
      <StatusRow
        icon="SELLAR_NEW"
        badge={<SellarCreditBadge text="Added to tab" />}
      >
        <span>
          <span className="font-semibold">Settled</span> on{" "}
          {dayjs(invoicePaidAt).format(DATE_FORMAT)}
        </span>
      </StatusRow>
    );
  }

  // Order has been processed (tab charged)
  if (orderHasBeenProcessed) {
    return (
      <StatusRow
        icon="SELLAR_NEW"
        badge={<SellarCreditBadge text="Added to tab" />}
      >
        <span>
          <span className="font-semibold">Added to tab</span> on{" "}
          {dayjs(sellarCreditPaymentCreatedAt).format(DATE_FORMAT)}
        </span>
      </StatusRow>
    );
  }

  if (preConfirmedStages.includes(stage)) {
    return (
      <StatusRow>
        <div className="flex flex-col gap-2">
          <span className="font-semibold">
            Add this Sellar Credit order to your tab
          </span>
          <span>
            Confirm this order with {order.Supplier.Business.name} and put it on
            your tab using the button below.
          </span>
          <SellarCreditAddToTabButton
            orderId={order.id}
            orderDeferredPaymentId={order.Payment?.deferredPaymentId ?? null}
            initiator={order.initiator}
            isDisabled={!business?.BusinessUser?.canAccessSellarCredit}
            ctaText="Add to tab"
          />
        </div>
      </StatusRow>
    );
  }

  return null;
};

// ─── Main component ───────────────────────────────────────────────────────────

type Props = {
  order: OrderGetResponseType;
  connection?: OrderContentConnectionsType;
};

export const ImplicitProformaPaymentStatus = ({ order, connection }: Props) => {
  const { business } = useSellarCurrentBusiness();
  const isBuyer = !!business?.Retailer;
  const isSupplier = !!business?.Supplier;
  const navigate = useNavigate();
  const isPaymentLinkRequestEnabled = useRemoteFeatureFlags().isActive(
    "checkoutPaymentLinkRequest",
  );

  const stage = getOrderStage(order);

  const isSellarCredit = order.paymentType === "sellarCredit";
  const isManualPaid =
    order.paymentType === "manual" &&
    !!(isBuyer ? order.retailerPaidAt : order.paidAt);
  const isCardPaymentWithDepositPaid =
    order.paymentType === "cardPaymentWithDeposit" &&
    order.paymentStatus === "paid";

  // "Marked as paid" needs the email of whoever marked it.
  const orderEventListQuery = useOrderEventList(
    {
      query: [{ type: "OrderId", modifier: "EQUALS", value: order.id }],
      sortBy: "createdAt",
      sortDir: "desc",
    },
    { enabled: isManualPaid || isCardPaymentWithDepositPaid },
  );
  const paidEvent = orderEventListQuery.data?.pages
    .flat()
    .find(
      (e) =>
        e.data.type === "paid" ||
        e.data.type === "remaining-balance-paid-manually",
    );

  const hasPaymentLink = Boolean(order.PaymentLink);
  const paymentLinkCode = order.PaymentLink?.code;
  const paymentLinkRequestedAt = order.PaymentLink?.updatedAt ?? null;

  // Card / deposit payments
  const paymentsQuery = usePaymentList(
    {
      query: [{ type: "OrderId", modifier: "EQUALS", value: order.id }],
    },
    {
      enabled:
        order.paymentType === "checkout" ||
        order.paymentType === "cardPaymentWithDeposit",
    },
  );
  const paymentList = paymentsQuery.data?.pages[0] ?? [];
  const payment = paymentList[0];

  const hasNoPaymentOrFailed =
    !payment || payment.status === "failed" || payment.status === "pending";

  const hasCheckoutPaymentFailed =
    (order.paymentType === "checkout" || order.paymentType === "stripe") &&
    hasNoPaymentOrFailed;

  const cardDepositPayment = paymentList.find(
    (p) => p.type === "cardDeposit" && p.status === "paid",
  );
  const hasCardDepositFailed =
    order.paymentType === "cardPaymentWithDeposit" &&
    !cardDepositPayment &&
    !paymentList.some(
      (p) => p.type === "cardDeposit" && p.status === "pending",
    );
  const hasRemainingBalanceFailed = !!(
    order.paymentType === "cardPaymentWithDeposit" &&
    order.paymentStatus !== "paid" &&
    cardDepositPayment &&
    paymentList.find(
      (p) =>
        p.type === null && (p.status === "failed" || p.status === "pending"),
    )
  );
  const hasRemainingBalancePaid =
    order.paymentType === "cardPaymentWithDeposit" &&
    !!cardDepositPayment &&
    (order.paymentStatus === "paid" ||
      paymentList.some((p) => p.status === "paid" && p.type === null));

  // Dialog state
  const [sendPaymentLinkOpen, setSendPaymentLinkOpen] = useState(false);
  const [requestCardPaymentOpen, setRequestCardPaymentOpen] = useState(false);
  const [paymentFailedOpen, setPaymentFailedOpen] = useState(false);
  const [depositFailedOpen, setDepositFailedOpen] = useState(false);
  const [remainingBalanceFailedOpen, setRemainingBalanceFailedOpen] =
    useState(false);
  const [paymentChangeOpen, setPaymentChangeOpen] = useState(false);

  const retailerInvoiceEmails = order.retailerInvoiceEmails?.length
    ? order.retailerInvoiceEmails
    : sanitizeEmailArray(order.Retailer.Business.invoiceEmails);

  const hasBankDetails =
    Boolean(order.Supplier.SupplierPaymentInformation?.bankAccountNumber) &&
    Boolean(order.Supplier.SupplierPaymentInformation?.bankSortCode);

  const paymentTerms = connection?.paymentTerms;

  const canRequestPayment =
    isSupplier &&
    !isSellarCredit &&
    !order.confirmedAt &&
    !order.dispatchedAt &&
    order.paymentStatus !== "paid" &&
    order.paymentType !== "cardPaymentWithDeposit" &&
    !order.stripeInvoiceId &&
    !hasCheckoutPaymentFailed &&
    !hasCardDepositFailed &&
    (isPaymentLinkRequestEnabled
      ? !!order.Supplier.stripeAccountId && !hasPaymentLink
      : !order.paymentDue || paymentTerms === "paymentOnDelivery");

  const canSendPostFinalisePaymentLink =
    isSupplier &&
    !isSellarCredit &&
    order.confirmedAt &&
    !order.dispatchedAt &&
    order.paymentStatus !== "paid" &&
    order.paymentType === "manual" &&
    (paymentTerms === "proforma" || paymentTerms === "credit") &&
    isPaymentLinkRequestEnabled &&
    !!order.Supplier.stripeAccountId;

  // ─── Modals (always rendered so state is preserved) ─────────────────────────
  const modals = (
    <>
      <SupplierOnly>
        <SendPaymentLinkModal
          active={sendPaymentLinkOpen}
          onDismiss={() => setSendPaymentLinkOpen(false)}
          key={order.id}
          orderId={order.id}
          retailerInvoiceEmails={retailerInvoiceEmails}
          hasBankDetails={hasBankDetails}
          invoicesEnabled={order.Supplier.invoicesEnabled}
          invoiceCreationStage={order.Supplier.invoiceCreationStage}
        />
        {order.paymentType !== "checkout" &&
          order.paymentType !== "cardPaymentWithDeposit" && (
            <OrderRequestCardPaymentModal
              active={requestCardPaymentOpen}
              onDismiss={() => setRequestCardPaymentOpen(false)}
              orderId={order.id}
              buyerName={order.Retailer.Business.name}
              onSuccess={() => {}}
              stripeAccountId={order.Supplier.stripeAccountId}
              paymentType={order.paymentType}
              hasNoDraughtDutyRelief={order.hasNoDraughtDutyRelief}
            />
          )}
        <PaymentChangeDialog
          orderId={order.id}
          active={paymentChangeOpen}
          onDismiss={() => setPaymentChangeOpen(false)}
          stripeAccountId={order.Supplier.stripeAccountId}
        />
      </SupplierOnly>
      <BuyerOnly>
        <PaymentFailedDialog
          orderId={order.id}
          active={paymentFailedOpen}
          onDismiss={() => setPaymentFailedOpen(false)}
        />
        <CardPaymentWithDepositFailedDialog
          orderId={order.id}
          active={depositFailedOpen}
          onDismiss={() => setDepositFailedOpen(false)}
        />
        <CardChargeRemainingBalancePaymentFailedDialog
          orderId={order.id}
          active={remainingBalanceFailedOpen}
          onDismiss={() => setRemainingBalanceFailedOpen(false)}
        />
      </BuyerOnly>
    </>
  );

  // ─── Early-exit states (single status row, no further rows needed) ───────────

  if (order.paymentStatus === "refunded") {
    return (
      <>
        <StatusRow
          icon="CREDIT_CARD_FAST_OUTLINE"
          badge={<BadgeIndigo icon="ARROW_U_LEFT_TOP">Refunded</BadgeIndigo>}
        >
          <span>
            <span className="font-semibold">Payment refunded</span> on{" "}
            {dayjs(order.cancelledAt).format(DATE_FORMAT)}
          </span>
        </StatusRow>
        {modals}
      </>
    );
  }

  if (order.paymentStatus === "void" || order.cancelledAt) {
    return null;
  }

  // Sellar Credit: delegate entirely to its own component.
  if (isSellarCredit) {
    return (
      <>
        <SellarCreditPaymentStatus
          order={order}
          isBuyer={isBuyer}
          isSupplier={isSupplier}
        />
        {modals}
      </>
    );
  }

  if (order.paymentType === "cardPayment") {
    return <RyftPaymentStatus order={order} isBuyer={isBuyer} />;
  }

  const paidAt = isBuyer ? order.retailerPaidAt : order.paidAt;

  if (paidAt && order.paymentType === "checkout") {
    return (
      <>
        <StatusRow
          icon="CREDIT_CARD_FAST_OUTLINE"
          badge={
            <BadgeGreen icon="FILE_DOCUMENT_CHECK_OUTLINE">Paid</BadgeGreen>
          }
        >
          <span>
            <span className="font-semibold">Paid by card</span> on{" "}
            {dayjs(paidAt).format(DATE_FORMAT)}
          </span>
        </StatusRow>
        {modals}
      </>
    );
  }

  if (paidAt && order.paymentType === "manual") {
    const markedPaidBy = paidEvent?.message.match(/[\w.-]+@[\w.-]+\.\w+/)?.[0];
    return (
      <>
        <StatusRow
          icon="CHECKBOX_MARKED_CIRCLE"
          badge={
            <BadgeGreen icon="FILE_DOCUMENT_CHECK_OUTLINE">Paid</BadgeGreen>
          }
        >
          <span>
            <span className="font-semibold">Marked as paid</span>{" "}
            {`on ${dayjs(paidAt).format(DATE_FORMAT)}${markedPaidBy ? ` by ${markedPaidBy}` : ""}`}
          </span>
        </StatusRow>
        {modals}
      </>
    );
  }

  // Card-with-deposit: full balance paid
  if (hasRemainingBalancePaid) {
    const isPaidManually =
      paidEvent?.data.type === "remaining-balance-paid-manually";
    return (
      <>
        <StatusRow
          icon="CREDIT_CARD_FAST_OUTLINE"
          badge={<BadgeGreen icon="CHECKBOX_MARKED_CIRCLE">Paid</BadgeGreen>}
        >
          <span>
            <span className="font-semibold">
              {isPaidManually ? "Paid manually" : "Paid"}
            </span>{" "}
            on {dayjs(order.paidAt).format(DATE_FORMAT)}
          </span>
        </StatusRow>
        {modals}
      </>
    );
  }

  // Card-with-deposit: deposit paid, waiting for remaining balance
  if (cardDepositPayment && !hasRemainingBalanceFailed) {
    return (
      <>
        <StatusRow
          icon="CREDIT_CARD_MARKER_OUTLINE"
          badge={
            <BadgeGreen icon="CHECKBOX_MARKED_CIRCLE">Deposit paid</BadgeGreen>
          }
        >
          <span>
            <span className="font-semibold">Deposit paid</span> of{" "}
            {formatCurrency(cardDepositPayment.amount, order.currency)} on{" "}
            {dayjs(cardDepositPayment.createdAt).format(DATE_FORMAT)}
          </span>
        </StatusRow>
        {modals}
      </>
    );
  }

  // ─── Actionable / multi-row states ───────────────────────────────────────────
  const rows: ReactElement[] = [];

  // Payment failed — buyer: retry CTA
  if (
    isBuyer &&
    (hasCheckoutPaymentFailed || hasCardDepositFailed) &&
    !hasPaymentLink
  ) {
    rows.push(
      <StatusRow key="failed-buyer">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">Payment failed</span>
          <span>
            Your payment to {order.Supplier.Business.name} has failed. Use the
            button below to retry your card payment.
          </span>
          <Button
            onClick={() =>
              hasCardDepositFailed
                ? setDepositFailedOpen(true)
                : setPaymentFailedOpen(true)
            }
          >
            Make payment
          </Button>
        </div>
      </StatusRow>,
    );
  }

  // Payment failed — supplier: edit method CTA
  const preConfirmedStages: OrderStage[] = ["Accepted", "Requested"];
  const canUpdatePayment = preConfirmedStages.includes(stage);
  if (
    isSupplier &&
    (hasCheckoutPaymentFailed || hasCardDepositFailed) &&
    !hasPaymentLink
  ) {
    rows.push(
      <StatusRow key="failed-supplier">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">Payment failed at checkout</span>
          <span>
            We were unable to process {order.Retailer.Business.name}&apos;s
            pro-forma card payment at checkout.
            {hasCheckoutPaymentFailed &&
              canUpdatePayment &&
              " You can issue them a card payment link by editing their payment method."}
          </span>
          {canUpdatePayment &&
            (hasCardDepositFailed ? (
              <span>
                Inform the buyer they must retry with a different card.
              </span>
            ) : (
              <Button onClick={() => setPaymentChangeOpen(true)}>
                Edit payment method
              </Button>
            ))}
        </div>
      </StatusRow>,
    );
  }

  // Remaining balance failed — buyer: retry CTA
  if (isBuyer && hasRemainingBalanceFailed) {
    rows.push(
      <StatusRow key="remaining-balance-failed">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">
            Remaining balance payment failed
          </span>
          <span>
            We were unable to process your final balance payment. Use the button
            below to retry your card payment.
          </span>
          <Button onClick={() => setRemainingBalanceFailedOpen(true)}>
            Make payment
          </Button>
        </div>
      </StatusRow>,
    );
  }

  // Payment request sent — supplier: re-send the request from the status row
  if (isSupplier && hasPaymentLink && !order.paidAt && paymentLinkRequestedAt) {
    rows.push(
      <StatusRow key="payment-link-sent" icon="CREDIT_CARD_FAST_OUTLINE">
        <div className="flex flex-col gap-2">
          <span>
            <span className="font-semibold">Payment request sent</span> on{" "}
            {dayjs(paymentLinkRequestedAt).format(DATE_FORMAT)}
          </span>
          <Button onClick={() => setSendPaymentLinkOpen(true)}>
            Re-send request
          </Button>
        </div>
      </StatusRow>,
    );
  }

  // Payment requested — buyer: pay CTA
  if (isBuyer && hasPaymentLink && !paidAt) {
    rows.push(
      <StatusRow key="payment-link-buyer">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">Payment requested</span>
          <span>
            {order.Supplier.Business.name} have requested payment. Use the
            button below to make your payment.
          </span>
          <Button
            onClick={() => {
              if (!paymentLinkCode) return;
              navigate(PAYMENT_LINK_REF.href({ code: paymentLinkCode }));
            }}
          >
            Make payment
          </Button>
        </div>
      </StatusRow>,
    );
  }

  // Request / reminder — supplier: send payment link CTA
  if (
    order.price > 0 &&
    !hasPaymentLink &&
    (canRequestPayment || canSendPostFinalisePaymentLink)
  ) {
    rows.push(
      <StatusRow key="request-payment">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">Request payment</span>
          <span>
            Send {order.Retailer.Business.name} a payment request so they can
            pay by card.
          </span>
          <Button
            onClick={() =>
              isPaymentLinkRequestEnabled
                ? setSendPaymentLinkOpen(true)
                : setRequestCardPaymentOpen(true)
            }
          >
            Request payment
          </Button>
        </div>
      </StatusRow>,
    );
  }

  return (
    <>
      {rows.length > 0 && <div className="flex flex-col gap-4">{rows}</div>}
      {modals}
    </>
  );
};
